import React from 'react';
import {
  CompactTable,
  Flex,
  openNotificationWithIcon,
  OperationalStatusIcon,
  PageContent,
  useFetch,
  useUserinfo,
} from '@mss/mss-component-library';
import Button from 'jsx/common/Button';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Col, Dropdown, Menu, Modal, notification, Row, Tooltip } from 'antd';
import { Edit, Trash } from 'react-feather';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisV,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import Fetch from 'jsx/common/util/_fetch';

export type OPCServerCompact = {
  id: string;
  name: string;
  host: string;
  port: string;
  authentication_mode: string;
  last_connection_check_status: boolean;
  last_connection_check_error: string | null;
};

export type OPCServer = {
  id: string;
  site: string;
  created_at: string;
  updated_at: string;
  deleted: boolean;
  center_synced_at: string | null;
  name: string;
  host: string;
  port: string;
  security_policy: string;
  message_security_policy: string;
  authentication_mode: string;
  username?: string;
  has_certificate: boolean;
  has_private_key: boolean;
  last_connection_check_status: string | null;
  last_connection_checked_at: string | null;
};

export type OPCServerGroup = {
  id: string;
  name: string;
  primary: OPCServerCompact;
  secondary: OPCServerCompact | null;
};

// Detail shape returned by GET /api/opc-config-server-group/{id}/ — primary/secondary are full server objects
export type OPCServerGroupDetail = {
  id: string;
  name: string;
  primary: OPCServer;
  secondary: OPCServer | null;
};

type OPCServerRow = {
  key: string;
  groupId: string;
  groupName: string;
  rowSpan: number;
  serverId: string;
  serverName: string;
  role: 'primary' | 'secondary';
  host: string;
  port: string;
  authentication_mode: string;
  last_connection_check_status: boolean;
  last_connection_check_error: string | null;
};

function OPCServers() {
  const [hasPermission] = useUserinfo();
  const history = useHistory();
  const { url } = useRouteMatch();

  const [t] = useTranslation(['configuration', 'common', 'authorization']);

  const [{ data: opcGroups, loading }, refetchOPCGroups] = useFetch({
    url: '/api/opc-config-server-group/',
    defaultValue: [],
  });

  const showMenu =
    hasPermission(['change_opcservergroupconfig']) ||
    hasPermission(['delete_opcservergroupconfig']);

  const editOPCGroup = (groupId: string) => {
    history.push(`${url}/${groupId}`);
  };

  const getAuthenticationSettingsLabel = (authMode: string): string => {
    switch (authMode) {
      case 'anonymous':
        return t('None');
      case 'username_password':
        return t('Username/Password');
      default:
        return authMode || t('None');
    }
  };

  const deleteModal = async (group: OPCServerGroup) =>
    Modal.confirm({
      title: t('Are you sure you want to delete?'),
      icon: (
        <span className="anticon">
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            color="#fd9644"
            size="sm"
          />
        </span>
      ),
      content: (
        <div>
          <blockquote>{group.name}</blockquote>
          <p>{t('You are about to delete this OPC Server group and all its associated servers.')}</p>
        </div>
      ),
      onOk: async () => {
        try {
          const resp = await Fetch.delete(
            `/api/opc-config-server-group/${group.id}/`
          );
          if (resp.status === 204) {
            openNotificationWithIcon(
              notification.success,
              t(`OPC Server group deleted`),
              ''
            );
            refetchOPCGroups();
          } else {
            throw new Error();
          }
        } catch {
          openNotificationWithIcon(
            notification.error,
            t(`OPC Server group deletion has failed`),
            ''
          );
        }
      },
      okButtonProps: { danger: true },
    });

  const rows: OPCServerRow[] = (opcGroups as OPCServerGroup[]).flatMap(
    (group) => {
      const servers: Array<OPCServerCompact & { role: 'primary' | 'secondary' }> = [
        { ...group.primary, role: 'primary' },
        ...(group.secondary ? [{ ...group.secondary, role: 'secondary' as const }] : []),
      ];
      return servers.map((server, i) => ({
        key: server.id,
        groupId: group.id,
        groupName: group.name,
        rowSpan: i === 0 ? servers.length : 0,
        serverId: server.id,
        serverName: server.name,
        role: server.role,
        host: server.host,
        port: server.port,
        authentication_mode: server.authentication_mode,
        last_connection_check_status: server.last_connection_check_status,
        last_connection_check_error: server.last_connection_check_error,
      }));
    }
  );

  const groupsMap = React.useMemo(() => {
    const map: Record<string, OPCServerGroup> = {};
    (opcGroups as OPCServerGroup[]).forEach((g) => {
      map[g.id] = g;
    });
    return map;
  }, [opcGroups]);

  return (
    <PageContent
      title={t('OPC Server Groups')}
      actions={
        <Flex v="center">
          {hasPermission(['add_opcservergroupconfig']) && (
            <Button
              onClick={() => history.push(`${url}/add`)}
              data-ta="addOPCServer"
            >
              {t('+ ADD OPC SERVER')}
            </Button>
          )}
        </Flex>
      }
    >
      <CompactTable
        rowKey="key"
        onRow={(row: OPCServerRow) => {
          return {
            onClick: () => editOPCGroup(row.groupId),
          };
        }}
        loading={loading}
        dataSource={rows}
        columns={[
          {
            title: t('OPC Server group name'),
            dataIndex: 'groupName',
            key: 'groupName',
            width: 220,
            onCell: (row: OPCServerRow) => ({ rowSpan: row.rowSpan }),
          },
          {
            title: t('OPC Server name'),
            dataIndex: 'serverName',
            key: 'serverName',
            width: 220,
          },
          {
            title: t('Role'),
            dataIndex: 'role',
            key: 'role',
            width: 120,
            render: (role: string) =>
              role === 'primary' ? t('Primary') : t('Secondary'),
          },
          {
            title: t('Endpoint URL'),
            dataIndex: 'host',
            key: 'endpoint_url',
            width: 280,
            render: (host: string, row: OPCServerRow) =>
              `${host}:${row.port}`,
          },
          {
            title: t('common:Port'),
            dataIndex: 'port',
            key: 'port',
            width: 100,
          },
          {
            title: t('Authentication Settings'),
            dataIndex: 'authentication_mode',
            key: 'authentication_settings',
            width: 220,
            render: (authMode: string) =>
              getAuthenticationSettingsLabel(authMode),
          },
          {
            title: t('Connection status'),
            dataIndex: 'last_connection_check_status',
            key: 'connection_status',
            width: 180,
            render: (status: boolean, row: OPCServerRow) => {
              return (
                <Tooltip
                  style={{ lineHeight: 1 }}
                  title={
                    <div>
                      <Row>
                        <Col className="mr-1">
                          <small>
                            <b>{t('common:Status')}</b>:
                          </small>
                        </Col>
                        <Col>
                          <small>
                            {status
                              ? t('common:Connected')
                              : t('common:Disconnected')}
                          </small>
                        </Col>
                      </Row>
                      {!status && (
                        <Row>
                          <Col>
                            <small style={{ opacity: 0.85 }}>
                              {row.last_connection_check_error || t('No details available')}
                            </small>
                          </Col>
                        </Row>
                      )}
                    </div>
                  }
                  placement="right"
                >
                  <Flex h="center" v="center" className="ml-1 text-gray-500">
                    <OperationalStatusIcon
                      operationalStatus={'operational'}
                      heartbeatStatus={status ? 'up' : 'down'}
                    />
                  </Flex>
                </Tooltip>
              );
            },
          },
          {
            title: '',
            key: 'actions',
            width: 16,
            onCell: (row: OPCServerRow) => ({ rowSpan: row.rowSpan }),
            render: (_: any, row: OPCServerRow) => {
              if (row.rowSpan === 0) return null;
              const group = groupsMap[row.groupId];
              return showMenu ? (
                <div
                  onClick={e => {
                    e.stopPropagation();
                  }}
                >
                  <Dropdown
                    trigger={['click']}
                    overlay={
                      <Menu>
                        {hasPermission(['change_opcservergroupconfig']) && (
                          <Menu.Item
                            key="edit"
                            onClick={() => editOPCGroup(row.groupId)}
                          >
                            <Flex v="center">
                              <Edit size={14} className="mr-2" />
                              {t('common:Edit')}
                            </Flex>
                          </Menu.Item>
                        )}
                        {hasPermission(['delete_opcservergroupconfig']) && (
                          <Menu.Item
                            key="delete"
                            onClick={() => group && deleteModal(group)}
                          >
                            <Flex v="center">
                              <Trash size={14} className="mr-2" />
                              {t('common:Delete')}
                            </Flex>
                          </Menu.Item>
                        )}
                      </Menu>
                    }
                  >
                    <div style={{ width: 32, textAlign: 'center' }}>
                      <FontAwesomeIcon icon={faEllipsisV} size="sm" />
                    </div>
                  </Dropdown>
                </div>
              ) : (
                <></>
              );
            },
          },
        ]}
      />
    </PageContent>
  );
}

export default OPCServers;
